/**
 * Export and import a backup.
 *
 * Until sync exists this is the only route work has off an iPad. Import shows
 * what it is about to do and waits for a decision — restoring an old backup
 * should never silently roll back work done since.
 */

import { useRef, useState } from 'react';

import {
  applyImport,
  describePlan,
  exportBackup,
  parseBackup,
  planImport,
  serializeBackup,
  type ImportPlan,
} from '../storage/backup';
import { repositories } from '../storage/repositories';
import { createBackend } from '../curriculum/progress';
import {
  estimateStorage,
  formatBytes,
  requestPersistence,
  type StorageEstimate,
} from '../storage/persistence';
import { Button } from '../design/Button';

export function BackupControls({ onImported }: { onImported?: () => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [plan, setPlan] = useState<ImportPlan | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [estimate, setEstimate] = useState<StorageEstimate | null>(null);

  const doExport = async () => {
    const repos = repositories();
    const progress = await createBackend().load();
    const backup = await exportBackup(repos, progress);
    const blob = new Blob([serializeBackup(backup)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `musicmentor-backup-${new Date().toISOString().slice(0, 10)}.json`;
    link.click();
    URL.revokeObjectURL(url);
    setMessage(`Exported ${backup.documents.length} document(s).`);
  };

  const chooseFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      const backup = parseBackup(await file.text());
      setPlan(await planImport(backup, repositories()));
      setMessage(null);
    } catch (error) {
      setPlan(null);
      setMessage((error as Error).message);
    }
  };

  const confirmImport = async () => {
    if (!plan) return;
    await applyImport(plan, repositories());
    setMessage(`Imported: ${describePlan(plan)}.`);
    setPlan(null);
    onImported?.();
  };

  const askToKeep = async () => {
    // Asked from a click, never on load, and framed as offline reliability.
    const granted = await requestPersistence();
    setEstimate(await estimateStorage());
    setMessage(
      granted
        ? 'This browser will now keep your work even when storage runs low.'
        : 'The browser declined. Your work is still saved — it is just more likely to be cleared if space runs out.',
    );
  };

  const showUsage = async () => setEstimate(await estimateStorage());

  return (
    <div className="backup-controls">
      <div className="backup-row">
        <Button variant="secondary" icon="download" onClick={() => void doExport()}>
          Export backup
        </Button>
        <Button variant="secondary" icon="upload" onClick={() => fileRef.current?.click()}>
          Import backup
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept="application/json"
          style={{ display: 'none' }}
          onChange={chooseFile}
        />
        <Button variant="tertiary" onClick={() => void askToKeep()}>
          Keep my work offline
        </Button>
        <Button variant="tertiary" onClick={() => void showUsage()}>
          Storage used
        </Button>
      </div>

      {plan ? (
        <div className="backup-plan" role="group" aria-label="Confirm import">
          <p>
            This backup contains: <strong>{describePlan(plan)}</strong>.
          </p>
          {plan.replace.length ? (
            <p className="caveat">
              {plan.replace.length} document(s) here will be replaced by a newer copy from the
              backup. Anything on this iPad that is already newer is left alone.
            </p>
          ) : null}
          <div className="backup-row">
            <Button variant="primary" onClick={() => void confirmImport()}>
              Import
            </Button>
            <Button variant="tertiary" onClick={() => setPlan(null)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      {estimate ? (
        <p className="fine">
          Using {formatBytes(estimate.usedBytes)}
          {estimate.quotaBytes ? ` of about ${formatBytes(estimate.quotaBytes)}` : ''}.{' '}
          {estimate.persisted
            ? 'This browser has agreed to keep it.'
            : 'The browser may clear it if space runs low.'}
        </p>
      ) : null}

      {message ? (
        <p className="fine" role="status">
          {message}
        </p>
      ) : null}

      <p className="fine">
        Nothing here leaves this iPad. There is no account and no server yet — a backup file is
        the only way your work travels.
      </p>
    </div>
  );
}
